import { z } from "zod";

// [x, y] or [x, y, facingDeg] — metres, facing in degrees (0=+x up-ice, CCW+, 90=judges)
export const Pos = z.union([
  z.tuple([z.number(), z.number()]),
  z.tuple([z.number(), z.number(), z.number()]),
]);

export const Facing = z.union([
  z.enum(["tangent", "hold", "spin", "reverse", "flip"]),
  z.number(),
  z.object({ around: z.tuple([z.number(), z.number()]) }),
  z.record(z.number()),
]);

export const Move = z
  .object({
    id: z.string(),
    startSec: z.number(),
    durationMs: z.number().optional(),
    type: z.enum(["path", "rotate", "hold"]),
    group: z.string().optional(),
    skaters: z.array(z.string()).optional(),
    to: z.record(z.tuple([z.number(), z.number()])).optional(),
    waypoints: z.record(z.array(z.tuple([z.number(), z.number()]))).optional(),
    arcM: z.number().optional(),
    ease: z.string().optional(),
    cx: z.number().optional(),
    cy: z.number().optional(),
    deg: z.number().optional(),
    rateDegPerSec: z.number().optional(),
    facing: Facing.optional(),
  })
  .refine((m) => m.group || m.skaters, {
    message: "move must have `group` or `skaters`",
  });

export const Section = z.object({
  id: z.string(),
  label: z.string(),
  atSec: z.number(),
  status: z.enum(["final", "draft"]).default("final"),
});

export const Overlay = z.object({
  fromSec: z.number(),
  toSec: z.number(),
  lines: z.array(z.array(z.string())).optional(),
  circles: z.array(z.array(z.string())).optional(),
});

export const Step = z.object({
  atSec: z.number(),
  text: z.string(),
  group: z.string().optional(),
  skaters: z.array(z.string()).optional(),
});

export const Highlight = z.object({
  id: z.string(),
  fromSec: z.number(),
  toSec: z.number(),
  label: z.string().optional(),
  skaters: z.array(z.string()).optional(),
  effect: z.enum(["pulse", "glow", "icon", "spotlight"]).optional(),
  icon: z.string().optional(),
});

export const Performance = z.object({
  schemaVersion: z.string(),
  meta: z.object({
    id: z.string(),
    team: z.string().optional(),
    theme: z.string().optional(),
    discipline: z.string().optional(),
    season: z.string().optional(),
    author: z.string().optional(),
    updated: z.string().optional(),
    rink: z.string(),
    defaultMarkings: z.string().optional(),
    notes: z.string().optional(),
  }),
  music: z.object({
    src: z.string(),
    durationSec: z.number(),
    offsetSec: z.number().default(0),
  }),
  skaters: z.array(z.object({ id: z.string(), name: z.string(), color: z.string() })),
  groups: z
    .array(z.object({ id: z.string(), label: z.string(), skaters: z.array(z.string()) }))
    .default([]),
  sections: z.array(Section),
  startPositions: z.record(Pos),
  moves: z.array(Move),
  overlays: z.array(Overlay).default([]),
  steps: z.array(Step).default([]),
  highlights: z.array(Highlight).default([]),
});

export const Rink = z.object({
  id: z.string(),
  label: z.string().optional(),
  lengthM: z.number(),
  widthM: z.number(),
  cornerRadiusM: z.number(),
  judgesSide: z.string().optional(),
});

export const MarkingElement = z.union([
  z.object({ type: z.literal("line"), from: z.tuple([z.number(), z.number()]), to: z.tuple([z.number(), z.number()]), color: z.string().optional() }),
  z.object({ type: z.literal("circle"), center: z.tuple([z.number(), z.number()]), rM: z.number(), color: z.string().optional() }),
  z.object({ type: z.literal("dot"), center: z.tuple([z.number(), z.number()]), rM: z.number(), color: z.string().optional() }),
  z.object({ type: z.literal("grid"), spacingM: z.number(), color: z.string().optional() }),
]);
export const MarkingSet = z.object({ label: z.string(), elements: z.array(MarkingElement) });
export const Markings = z.record(MarkingSet);

export type Performance = z.infer<typeof Performance>;
export type Move = z.infer<typeof Move>;
export type Section = z.infer<typeof Section>;
export type Step = z.infer<typeof Step>;
export type Highlight = z.infer<typeof Highlight>;
export type Overlay = z.infer<typeof Overlay>;
export type Rink = z.infer<typeof Rink>;
export type Markings = z.infer<typeof Markings>;
export type MarkingElement = z.infer<typeof MarkingElement>;
